/**
 * Coordinate conventions (docs/dev-notes/scene-frame.md).
 *
 * Pipeline files (OBJ vertices, measurements.json) are in mm with (x, y, z) = (i·vi, j·vj, k·vk):
 * voxel index times spacing on the cropped grid, origin at the centre of voxel 0.
 *
 * The `.r8` volume is a 3D texture of size (W, H, D) = (Ni, Nk, Nj): texture x = voxel i,
 * y = voxel k (slices, "up"), z = voxel j. Voxel (i, j, k) is sampled at
 * uvw = ((i+0.5)/W, (k+0.5)/H, (j+0.5)/D).
 *
 * The scene frame is the desktop app's Unity frame: X = i, Y = k (up), Z = j, in mm. A file
 * point (x, y, z) maps to scene (x, z, y). Unity is left-handed and three.js right-handed, so
 * the root group carries ONE handedness flip (scale z = -1) and nothing else is re-oriented.
 * The root also scales mm to Unity units (1 unit = 100 mm) so camera speeds and clip distances
 * match the desktop constants.
 */
import * as THREE from 'three';

export const MM_PER_UNIT = 100;
export const UNIT_PER_MM = 1 / MM_PER_UNIT;

/** File frame (mm) -> scene group frame (mm, before the root scale/flip). */
export function fileToScene(x: number, y: number, z: number, out = new THREE.Vector3()): THREE.Vector3 {
  return out.set(x, z, y);
}

export function fileVecToScene(v: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
  return out.set(v.x, v.z, v.y);
}

/** Direction vectors use the same axis swap (it is a pure permutation). */
export const fileDirToScene = fileVecToScene;

export interface VolumeGrid {
  dims: [number, number, number]; // texture (W, H, D)
  voxelSize: [number, number, number]; // mm per texel along texture x, y, z
}

/** Extent of the volume box in scene mm along x, y, z. */
export function volumeExtentMm(grid: VolumeGrid): THREE.Vector3 {
  return new THREE.Vector3(
    grid.dims[0] * grid.voxelSize[0],
    grid.dims[1] * grid.voxelSize[1],
    grid.dims[2] * grid.voxelSize[2],
  );
}

/** Centre of the volume box in scene mm: the box spans [-v/2, (N-1/2)v] on each axis so that
 * the centre of voxel 0 sits at the origin, where the file frame's origin is. */
export function volumeCenterMm(grid: VolumeGrid): THREE.Vector3 {
  const e = volumeExtentMm(grid);
  return new THREE.Vector3(
    e.x / 2 - grid.voxelSize[0] / 2,
    e.y / 2 - grid.voxelSize[1] / 2,
    e.z / 2 - grid.voxelSize[2] / 2,
  );
}

/** Scene mm -> uvw in [0,1]^3. */
export function sceneToUvw(p: THREE.Vector3, grid: VolumeGrid, out = new THREE.Vector3()): THREE.Vector3 {
  return out.set(
    (p.x / grid.voxelSize[0] + 0.5) / grid.dims[0],
    (p.y / grid.voxelSize[1] + 0.5) / grid.dims[1],
    (p.z / grid.voxelSize[2] + 0.5) / grid.dims[2],
  );
}

export function uvwToScene(u: THREE.Vector3, grid: VolumeGrid, out = new THREE.Vector3()): THREE.Vector3 {
  return out.set(
    (u.x * grid.dims[0] - 0.5) * grid.voxelSize[0],
    (u.y * grid.dims[1] - 0.5) * grid.voxelSize[1],
    (u.z * grid.dims[2] - 0.5) * grid.voxelSize[2],
  );
}

/** Patient directions of the scene axes (Unity local axes). Defaults from the desktop app when
 * the export carries no patient_orientation. */
export interface AxisLabels {
  xPositive: string;
  xNegative: string;
  yPositive: string;
  yNegative: string;
  zPositive: string;
  zNegative: string;
}

export const DEFAULT_AXIS_LABELS: AxisLabels = {
  xPositive: 'L',
  xNegative: 'R',
  yPositive: 'S',
  yNegative: 'I',
  zPositive: 'P',
  zNegative: 'A',
};

export function axisLabelsFromOrientation(po: unknown): AxisLabels {
  const axes = (po as { unity_local_axes?: Record<string, string> } | null)?.unity_local_axes;
  if (!axes) return DEFAULT_AXIS_LABELS;
  return {
    xPositive: axes.x_positive ?? 'L',
    xNegative: axes.x_negative ?? 'R',
    yPositive: axes.y_positive ?? 'S',
    yNegative: axes.y_negative ?? 'I',
    zPositive: axes.z_positive ?? 'P',
    zNegative: axes.z_negative ?? 'A',
  };
}
